/**
 * Sonic Identity™ Configuration
 *
 * This file contains all configurable options for the Sonic Identity product.
 * Prices are in USD. Update this file to change pricing without code changes.
 */

// ============================================
// PHYSICAL ART EDITIONS (Fixed-price SKUs)
// ============================================
export const SONIC_PHYSICAL_EDITIONS = [
  // Small — 50 × 40 cm
  {
    id: 'small-paper-framed',
    size: 'Small',
    dimensions: '50 × 40 cm',
    width: 50,
    height: 40,
    material: 'Fine Art Paper',
    materialId: 'paper',
    framing: 'Framed',
    isFramed: true,
    price: 199,
    notes: 'Gift-ready',
    isRecommended: false,
    productionDays: '5-7',
  },
  {
    id: 'small-canvas-framed',
    size: 'Small',
    dimensions: '50 × 40 cm',
    width: 50,
    height: 40,
    material: 'Canvas',
    materialId: 'canvas',
    framing: 'Framed',
    isFramed: true,
    price: 279,
    notes: 'Classic',
    isRecommended: false,
    productionDays: '7-10',
  },
  {
    id: 'small-aluminium-unframed',
    size: 'Small',
    dimensions: '50 × 40 cm',
    width: 50,
    height: 40,
    material: 'Aluminium (Silver)',
    materialId: 'aluminium-silver',
    framing: 'Unframed',
    isFramed: false,
    price: 399,
    notes: 'Premium',
    isRecommended: false,
    productionDays: '7-10',
  },
  // Medium — 80 × 60 cm
  {
    id: 'medium-paper-framed',
    size: 'Medium',
    dimensions: '80 × 60 cm',
    width: 80,
    height: 60,
    material: 'Fine Art Paper',
    materialId: 'paper',
    framing: 'Framed',
    isFramed: true,
    price: 349,
    notes: 'Classic',
    isRecommended: false,
    productionDays: '5-7',
  },
  {
    id: 'medium-canvas-framed',
    size: 'Medium',
    dimensions: '80 × 60 cm',
    width: 80,
    height: 60,
    material: 'Canvas',
    materialId: 'canvas',
    framing: 'Framed',
    isFramed: true,
    price: 449,
    notes: 'Warm',
    isRecommended: false,
    productionDays: '7-10',
  },
  {
    id: 'medium-aluminium-unframed',
    size: 'Medium',
    dimensions: '80 × 60 cm',
    width: 80,
    height: 60,
    material: 'Aluminium (Silver)',
    materialId: 'aluminium-silver',
    framing: 'Unframed',
    isFramed: false,
    price: 649,
    notes: 'Recommended',
    isRecommended: true,
    productionDays: '7-10',
  },
  // Large — 100 × 70 cm
  {
    id: 'large-paper-framed',
    size: 'Large',
    dimensions: '100 × 70 cm',
    width: 100,
    height: 70,
    material: 'Fine Art Paper',
    materialId: 'paper',
    framing: 'Framed',
    isFramed: true,
    price: 749,
    notes: 'Gallery',
    isRecommended: false,
    productionDays: '7-10',
  },
  {
    id: 'large-canvas-framed',
    size: 'Large',
    dimensions: '100 × 70 cm',
    width: 100,
    height: 70,
    material: 'Canvas',
    materialId: 'canvas',
    framing: 'Framed',
    isFramed: true,
    price: 949,
    notes: 'Statement',
    isRecommended: false,
    productionDays: '10-14',
  },
  {
    id: 'large-aluminium-unframed',
    size: 'Large',
    dimensions: '100 × 70 cm',
    width: 100,
    height: 70,
    material: 'Aluminium (Silver)',
    materialId: 'aluminium-silver',
    framing: 'Unframed',
    isFramed: false,
    price: 1249,
    notes: 'Statement',
    isRecommended: false,
    productionDays: '10-14',
  },
] as const;

export type SonicPhysicalEditionId = typeof SONIC_PHYSICAL_EDITIONS[number]['id'];

// ============================================
// SIZE OPTIONS (Legacy - for UI display)
// ============================================
export const SONIC_SIZES = [
  {
    id: 'small',
    label: 'Small',
    dimensions: '50 × 40 cm',
    width: 50,
    height: 40,
    basePrice: 199,
  },
  {
    id: 'medium',
    label: 'Medium',
    dimensions: '80 × 60 cm',
    width: 80,
    height: 60,
    basePrice: 349,
    isPopular: true,
  },
  {
    id: 'large',
    label: 'Large',
    dimensions: '100 × 70 cm',
    width: 100,
    height: 70,
    basePrice: 749,
  },
] as const;

export type SonicSizeId = typeof SONIC_SIZES[number]['id'];

// ============================================
// MATERIAL OPTIONS
// ============================================
export const SONIC_MATERIALS = [
  {
    id: 'paper',
    label: 'Fine Art Paper',
    description: 'Archival fine art print',
    priceModifier: 0,
    productionDays: '5-7',
  },
  {
    id: 'canvas',
    label: 'Canvas',
    description: 'Gallery-ready finish with warm texture',
    priceModifier: 100,
    productionDays: '7-10',
  },
  {
    id: 'aluminium-silver',
    label: 'Aluminium (Silver)',
    description: 'Premium metallic finish with exceptional durability',
    priceModifier: 300,
    productionDays: '7-10',
  },
] as const;

export type SonicMaterialId = typeof SONIC_MATERIALS[number]['id'];

// ============================================
// FRAMING OPTIONS (Included in editions)
// ============================================
export const SONIC_FRAMING = {
  price: 0, // Now included in edition price
  addedWidth: 5, // cm added to each side
  addedHeight: 5, // cm added to each side
  productionDays: '0', // Included in edition time
  recommendedForSizes: ['small', 'medium'] as SonicSizeId[],
} as const;

// ============================================
// COLOR OPTIONS
// ============================================
export const SONIC_COLOR_PRICING = {
  colorfulPrice: 0, // Colorful is included at no extra charge (matches Black & White)
  freeColorId: 'black', // Black/white is free
} as const;

/**
 * Calculate color pricing
 * @param selectedColors - Array of color IDs
 * @returns Price for colors — currently 0 in all cases; both Black & White and
 *          Colorful are included in the base package price.
 */
export function calculateColorPrice(_selectedColors: string[]): number {
  return SONIC_COLOR_PRICING.colorfulPrice;
}

// ============================================
// FORMAT TYPE OPTIONS (Digital / Physical / Phygital)
// ============================================
export const SONIC_FORMAT_TYPES = [
  {
    id: 'digital',
    label: 'Digital',
    description: 'Screen-resolution artwork, delivered within 3 working days',
    startingPrice: 99,
  },
  {
    id: 'physical',
    label: 'Physical',
    description: 'Archival printed artwork delivered to you',
    startingPrice: 199, // Small size + paper material
  },
  {
    id: 'phygital',
    label: 'Phygital',
    description: 'Both digital file and physical print',
    startingPrice: 298, // Digital ($99) + Small physical ($199)
    isPopular: true,
  },
] as const;

export type SonicFormatTypeId = typeof SONIC_FORMAT_TYPES[number]['id'];

// ============================================
// DELIVERY OPTIONS
// ============================================
export const SONIC_DELIVERY = {
  standard: {
    id: 'standard',
    label: 'Standard Digital Delivery',
    description: 'Within 3 working days',
    workingDays: 3,
    price: 0, // Included
  },
  fastTrack: {
    id: 'fast-track',
    label: 'Fast-Track Digital Delivery',
    description: 'Within 1 working day',
    workingDays: 1,
    price: 79, // Additional fee
  },
} as const;

export type SonicDeliveryId = 'standard' | 'fast-track';

// ============================================
// ACTIVE CATALOG
// ============================================
/**
 * Which catalog the storefront sells.
 *
 * Must equal CURRENT_PRICE_CATALOG_VERSION in the backend's sonic-pricing and
 * CURRENT_CATALOG_VERSION in gelato.config — the backend recomputes every total
 * and ignores what the client sends, so a mismatch means the customer is shown
 * one price and charged another. A test asserts all three agree.
 */
// Typed as `number`, not the literal `1`, so version comparisons elsewhere
// are not narrowed to a constant and flagged as unreachable.
export const SONIC_ACTIVE_CATALOG_VERSION: number = 2;

// ============================================
// CATALOG V2 — the final price list
// ============================================
// Defined alongside v1 rather than replacing it. Display, price authority and
// the Gelato catalog must switch together: the backend recomputes every total
// and ignores what the client sends, so showing v2 prices while the authority
// still holds v1 rules would quietly overcharge. The bundle restructure flips
// all three at once.
//
// Sizes are landscape, matching the products and the artwork's 4:3 ratio.
// Canvas is not sold in v2.

/** The single product: three interpretations plus one framed print. */
export const SONIC_BUNDLE_V2 = {
  id: 'sonic-identity',
  name: 'Sonic Identity™',
  price: 399,
  includes: [
    'Three artist-curated digital interpretations',
    'Your chosen interpretation as a Digital Edition',
    'One Framed Fine Art Print of your chosen image, 40 × 30 cm landscape, black slim wooden frame',
  ],
  /** The print included in the bundle, as an edition id below. */
  // Distinct from v1's 'small-paper-framed', which is a 50 x 40 cm white-framed
  // product at $199. Both arrays exist until the switch, and an id shared
  // between them would resolve to the wrong product and price.
  includedEditionId: 'small-paper-black-wood',
  workingDays: 3,
} as const;

export const SONIC_DELIVERY_V2 = {
  standard: {
    id: 'standard',
    label: 'Standard Creation',
    description: 'Within 3 working days',
    workingDays: 3,
    price: 0,
  },
  express: {
    id: 'express',
    label: 'Express Creation',
    description: 'Within 1 working day',
    workingDays: 1,
    price: 99,
  },
} as const;

export const SONIC_MATERIALS_V2 = [
  {
    id: 'paper',
    label: 'Fine Art Paper',
    description: 'Archival fine art paper in a black slim wooden frame, ready to hang',
    productionDays: '5-7',
  },
  {
    id: 'aluminium',
    label: 'Aluminium',
    description: 'Contemporary and luminous, in a black aluminium frame, ready to hang',
    productionDays: '7-10',
  },
] as const;

export type SonicMaterialV2Id = typeof SONIC_MATERIALS_V2[number]['id'];

export const SONIC_SIZES_V2 = [
  { id: 'small', label: 'Small', dimensions: '40 × 30 cm landscape', width: 40, height: 30 },
  { id: 'medium', label: 'Medium', dimensions: '60 × 45 cm landscape', width: 60, height: 45 },
  { id: 'large', label: 'Large', dimensions: '80 × 60 cm landscape', width: 80, height: 60 },
] as const;

export type SonicSizeV2Id = typeof SONIC_SIZES_V2[number]['id'];

/**
 * The five sellable physical editions. Statement aluminium exists as a Gelato
 * product but carries no list price, so it is not offered.
 */
export const SONIC_PHYSICAL_EDITIONS_V2 = [
  {
    id: 'small-paper-black-wood',
    sizeId: 'small',
    size: 'Small',
    dimensions: '40 × 30 cm landscape',
    material: 'Fine Art Paper',
    materialId: 'paper',
    framing: 'Black slim wooden frame',
    isFramed: true,
    price: 149,
    notes: 'The size included in every Sonic Identity™',
    isRecommended: false,
    productionDays: '5-7',
  },
  {
    id: 'medium-paper-black-wood',
    sizeId: 'medium',
    size: 'Medium',
    dimensions: '60 × 45 cm landscape',
    material: 'Fine Art Paper',
    materialId: 'paper',
    framing: 'Black slim wooden frame',
    isFramed: true,
    price: 249,
    notes: '',
    isRecommended: true,
    productionDays: '5-7',
  },
  {
    id: 'large-paper-black-wood',
    sizeId: 'large',
    size: 'Large',
    dimensions: '80 × 60 cm landscape',
    material: 'Fine Art Paper',
    materialId: 'paper',
    framing: 'Black slim wooden frame',
    isFramed: true,
    price: 349,
    notes: '',
    isRecommended: false,
    productionDays: '5-7',
  },
  {
    id: 'small-aluminium-black',
    sizeId: 'small',
    size: 'Small',
    dimensions: '40 × 30 cm landscape',
    material: 'Aluminium',
    materialId: 'aluminium',
    framing: 'Black aluminium frame',
    isFramed: true,
    price: 349,
    notes: '',
    isRecommended: false,
    productionDays: '7-10',
  },
  {
    id: 'medium-aluminium-black',
    sizeId: 'medium',
    size: 'Medium',
    dimensions: '60 × 45 cm landscape',
    material: 'Aluminium',
    materialId: 'aluminium',
    framing: 'Black aluminium frame',
    isFramed: true,
    price: 449,
    notes: '',
    isRecommended: false,
    productionDays: '7-10',
  },
] as const;

export type SonicPhysicalEditionV2Id = typeof SONIC_PHYSICAL_EDITIONS_V2[number]['id'];

/**
 * What an upgrade to the bundle's included print costs the customer.
 *
 * The $399 already bought a framed 40 x 30, so a larger size or aluminium
 * costs the difference. Mirrors recomputeSonicUpgradePrice on the backend,
 * which is the authority — a consistency test asserts they agree.
 */
export function calculateSonicUpgradeV2(input: {
  sizeId: string;
  materialId: string;
}): { listPrice: number; credit: number; total: number } {
  const edition = SONIC_PHYSICAL_EDITIONS_V2.find(
    (e) => e.sizeId === input.sizeId && e.materialId === input.materialId
  );
  if (!edition) {
    throw new Error(
      `No edition for size "${input.sizeId}" and material "${input.materialId}"`
    );
  }
  const included = SONIC_PHYSICAL_EDITIONS_V2.find(
    (e) => e.id === SONIC_BUNDLE_V2.includedEditionId
  );
  if (!included) {
    throw new Error("The bundle's included edition is not in the edition list");
  }
  return {
    listPrice: edition.price,
    credit: included.price,
    // Never negative: a cheaper edition is not a refund.
    total: Math.max(0, edition.price - included.price),
  };
}

/** Anything outside the five editions is an enquiry, not a purchase. */
export const SONIC_CUSTOM_SIZE_V2 = {
  label: 'Custom Size',
  price: null,
  cta: 'Contact artist',
} as const;

/**
 * The studio's working schedule, as stated to the customer.
 *
 * These are the customer-facing halves of rules the backend already enforces:
 * `DEFAULT_CUTOFF_HOUR` in working-days.ts and the caps in
 * studio-capacity.service.ts. They were enforced but never stated — the site
 * said "after the daily cut-off" without ever naming a time, so a customer had
 * no way to know whether ordering now bought them today's queue or tomorrow's.
 *
 * A test asserts these agree with the backend constants. A promise the
 * scheduler does not keep is worse than no promise.
 */
export const SONIC_STUDIO_SCHEDULE_V2 = {
  timeZoneLabel: 'Dubai time',
  cutoffHour: 14,
  cutoffLabel: '2 pm Dubai time',
  dailyOrderCap: 3,
  dailyExpressCap: 1,
} as const;

// ============================================
// SHIPPING ESTIMATES
// ============================================
const SONIC_SHIPPING_V1 = {
  regions: [
    { id: 'us-uk-eu', label: 'US / UK / EU', days: '3-7' },
    { id: 'gcc', label: 'GCC', days: '2-5' },
    { id: 'row', label: 'Rest of World', days: '5-10' },
  ],
  disclaimer: 'Estimated delivery shown at checkout. Actual times may vary.',
} as const;

// The v1 windows were regional guesses that did not survive contact with real
// Gelato quotes — the GCC was quoted 2-5 days while Gelato ships the UAE from
// Germany in 13-22. v2 quotes what the studio can actually keep: the UAE is
// printed and delivered locally, everything else goes through Gelato.
const SONIC_SHIPPING_V2 = {
  regions: [
    { id: 'uae', label: 'United Arab Emirates', days: '3-7' },
    { id: 'international', label: 'International', days: '7-14' },
  ],
  disclaimer: 'Estimated delivery shown at checkout. Actual times may vary.',
} as const;

export const SONIC_SHIPPING =
  SONIC_ACTIVE_CATALOG_VERSION >= 2 ? SONIC_SHIPPING_V2 : SONIC_SHIPPING_V1;

/**
 * The fields every caller needs from an edition, in either catalogue.
 *
 * v1 and v2 carry extra fields of their own (v1 has width/height, v2 has
 * sizeId), but nothing that looks an edition up by id uses those.
 */
export interface SonicEditionSummary {
  id: string;
  size: string;
  dimensions: string;
  material: string;
  materialId: string;
  isFramed: boolean;
  price: number;
}

/** Editions belonging to the catalogue that is currently selling. */
export const SONIC_PHYSICAL_EDITIONS_ACTIVE: readonly SonicEditionSummary[] =
  SONIC_ACTIVE_CATALOG_VERSION >= 2 ? SONIC_PHYSICAL_EDITIONS_V2 : SONIC_PHYSICAL_EDITIONS;

/**
 * Sizes and materials of the catalogue that is currently selling.
 *
 * Every component that looked a size or material up by id was reaching for the
 * v1 arrays directly. The ids overlap — both catalogues have `small` and
 * `paper` — so the lookup succeeded and returned the wrong record instead of
 * failing: the print panel offered 50 × 40 cm at $199, and the checkout summary
 * quoted the same. Nothing type-checked as wrong, because both are valid
 * arrays of the same shape.
 *
 * Prefer these over the raw constants anywhere the customer is being shown what
 * they are buying. The v1 arrays remain exported for orders already sold under
 * that catalogue.
 */
export const SONIC_SIZES_ACTIVE: readonly {
  id: string;
  label: string;
  dimensions: string;
  width: number;
  height: number;
}[] = SONIC_ACTIVE_CATALOG_VERSION >= 2 ? SONIC_SIZES_V2 : SONIC_SIZES;

export const SONIC_MATERIALS_ACTIVE: readonly {
  id: string;
  label: string;
  description: string;
  productionDays: string;
}[] = SONIC_ACTIVE_CATALOG_VERSION >= 2 ? SONIC_MATERIALS_V2 : SONIC_MATERIALS;

/**
 * Resolve an edition id against the catalogue that is currently selling.
 *
 * The ids are catalogue-specific — v1 sells `small-paper-framed`, v2 sells
 * `small-paper-black-wood` — so looking one up in the wrong list finds nothing.
 * That is how the print page crashed: the store correctly defaulted to a v2 id
 * while `calculateSonicPrice` and `setPhysicalEdition` still searched the v1
 * array, and a miss threw rather than degraded.
 *
 * Anything that needs the edition a *paid order* was sold under must resolve
 * against that order's stored catalogueVersion instead, not this — the active
 * catalogue moves, a sold order must not.
 */
export function findSonicEdition(editionId: string | null | undefined) {
  if (!editionId) return undefined;
  return SONIC_PHYSICAL_EDITIONS_ACTIVE.find((e) => e.id === editionId);
}

/**
 * The expedited delivery option, under whichever catalogue is live.
 *
 * v1 called it Fast-Track at $79; v2 calls it Express Creation at $99. The
 * rename was carried in the catalogue but not in the UI, so order summaries,
 * wizard totals and the marketing page each kept their own hardcoded
 * "Fast-Track" label — and the web page went on advertising $79 while
 * checkout charged $99. Reading the label and price from here means a future
 * rename lands everywhere at once, or nowhere.
 */
export const SONIC_EXPRESS_OPTION =
  SONIC_ACTIVE_CATALOG_VERSION >= 2 ? SONIC_DELIVERY_V2.express : SONIC_DELIVERY.fastTrack;

// ============================================
// INFLUENCE TAGS (Predefined suggestions)
// ============================================
export const SONIC_INFLUENCE_TAGS = {
  places: [
    'Dubai', 'New York', 'London', 'Tokyo', 'Paris', 'Sydney',
    'Mumbai', 'Singapore', 'Los Angeles', 'Berlin', 'Hong Kong',
  ],
  cultures: [
    'Arabic', 'Japanese', 'Mediterranean', 'Nordic', 'Indian',
    'African', 'Latin American', 'European', 'Asian', 'Middle Eastern',
  ],
  passions: [
    'Music', 'Art', 'Nature', 'Technology', 'Fashion', 'Sports',
    'Travel', 'Food', 'Literature', 'Film', 'Photography',
  ],
  themes: [
    'Family', 'Love', 'Success', 'Adventure', 'Peace', 'Growth',
    'Heritage', 'Innovation', 'Spirituality', 'Freedom', 'Legacy',
  ],
} as const;

// ============================================
// VALIDATION RULES
// ============================================
export const SONIC_VALIDATION = {
  nameMinLength: 1,
  nameMaxLength: 100,
  voiceMaxDuration: 60, // seconds
  voiceMinDuration: 3, // seconds
  voiceMaxFileSizeMB: 25, // max audio file size in MB
  maxInfluenceTags: 10,
  minInfluenceTags: 1,
} as const;

// ============================================
// LEGAL / CONFIRMATION TEXT
// ============================================
export const SONIC_LEGAL = {
  confirmationCheckbox: `I understand this is a final, system-led artwork. Digital delivery is final. Physical production begins automatically upon order completion.`,
  noRevisions: `Sonic Identities™ are final system-led interpretations. Revisions are not included.`,
  supportNote: `Support is available for technical errors, damaged shipments, and printer defects only.`,
} as const;

// ============================================
// PRICE CALCULATOR (Using Physical Editions)
// ============================================
export function calculateSonicPrice(options: {
  editionId?: SonicPhysicalEditionId;
  // Legacy support
  sizeId?: SonicSizeId;
  materialId?: SonicMaterialId;
  withFraming?: boolean;
  deliveryId: SonicDeliveryId;
}): {
  basePrice: number;
  materialPrice: number;
  framingPrice: number;
  deliveryPrice: number;
  total: number;
  breakdown: { label: string; amount: number }[];
} {
  const delivery = options.deliveryId === 'fast-track'
    ? SONIC_DELIVERY.fastTrack
    : SONIC_DELIVERY.standard;

  // New edition-based pricing
  if (options.editionId) {
    // Resolve against the live catalogue, not v1: under v2 the store hands us
    // ids like `small-paper-black-wood`, which do not exist in the v1 array.
    const edition = findSonicEdition(options.editionId);
    if (!edition) {
      throw new Error(
        `Invalid edition selection: '${options.editionId}' is not in catalogue v${SONIC_ACTIVE_CATALOG_VERSION}`
      );
    }

    const deliveryPrice = delivery.price;
    const total = edition.price + deliveryPrice;

    const breakdown: { label: string; amount: number }[] = [
      { label: `${edition.size} ${edition.material} (${edition.dimensions})`, amount: edition.price },
    ];

    if (deliveryPrice > 0) {
      breakdown.push({ label: delivery.label, amount: deliveryPrice });
    }

    return {
      basePrice: edition.price,
      materialPrice: 0,
      framingPrice: 0,
      deliveryPrice,
      total,
      breakdown,
    };
  }

  // Legacy fallback - find matching edition
  const matchingEdition = SONIC_PHYSICAL_EDITIONS.find(e => {
    const sizeMatch = e.size.toLowerCase() === options.sizeId;
    const materialMatch = e.materialId === options.materialId;
    const framingMatch = e.isFramed === (options.withFraming ?? false);
    return sizeMatch && materialMatch && framingMatch;
  });

  if (matchingEdition) {
    const deliveryPrice = delivery.price;
    const total = matchingEdition.price + deliveryPrice;

    const breakdown: { label: string; amount: number }[] = [
      { label: `${matchingEdition.size} ${matchingEdition.material} (${matchingEdition.dimensions})`, amount: matchingEdition.price },
    ];

    if (deliveryPrice > 0) {
      breakdown.push({ label: delivery.label, amount: deliveryPrice });
    }

    return {
      basePrice: matchingEdition.price,
      materialPrice: 0,
      framingPrice: 0,
      deliveryPrice,
      total,
      breakdown,
    };
  }

  // Fallback to default edition (small paper framed)
  const defaultEdition = SONIC_PHYSICAL_EDITIONS[0];
  const deliveryPrice = delivery.price;
  const total = defaultEdition.price + deliveryPrice;

  return {
    basePrice: defaultEdition.price,
    materialPrice: 0,
    framingPrice: 0,
    deliveryPrice,
    total,
    breakdown: [
      { label: `${defaultEdition.size} ${defaultEdition.material}`, amount: defaultEdition.price },
    ],
  };
}

// ============================================
// ESTIMATED PRICE CALCULATOR
// ============================================
export function calculateEstimatedPrice(options: {
  formatTypeId: SonicFormatTypeId;
  editionId?: SonicPhysicalEditionId;
  sizeId?: SonicSizeId;
  materialId?: SonicMaterialId;
  withFraming?: boolean;
}): number {
  const digitalFormat = SONIC_FORMAT_TYPES.find(f => f.id === 'digital');
  const digitalPrice = digitalFormat?.startingPrice || 99;

  // For digital only, just return the digital price
  if (options.formatTypeId === 'digital') {
    return digitalPrice;
  }

  // Get physical price from edition
  let physicalPrice = 0;

  if (options.editionId) {
    const edition = SONIC_PHYSICAL_EDITIONS.find(e => e.id === options.editionId);
    if (edition) {
      physicalPrice = edition.price;
    }
  } else {
    // Find matching edition or use default (small paper framed = $199)
    const matchingEdition = SONIC_PHYSICAL_EDITIONS.find(e => {
      const sizeMatch = !options.sizeId || e.size.toLowerCase() === options.sizeId;
      const materialMatch = !options.materialId || e.materialId === options.materialId;
      return sizeMatch && materialMatch;
    });

    physicalPrice = matchingEdition?.price || SONIC_PHYSICAL_EDITIONS[0].price;
  }

  // For physical only, return just the physical price
  if (options.formatTypeId === 'physical') {
    return physicalPrice;
  }

  // For phygital, return digital + physical
  if (options.formatTypeId === 'phygital') {
    return digitalPrice + physicalPrice;
  }

  return 0;
}

// ============================================
// PRODUCTION TIME CALCULATOR
// ============================================
export function calculateProductionTime(options: {
  editionId?: SonicPhysicalEditionId;
  materialId?: SonicMaterialId;
  withFraming?: boolean;
}): string {
  // Use edition-based production time
  if (options.editionId) {
    const edition = SONIC_PHYSICAL_EDITIONS.find(e => e.id === options.editionId);
    if (edition) {
      return `${edition.productionDays} business days`;
    }
  }

  // Legacy fallback
  const material = SONIC_MATERIALS.find(m => m.id === options.materialId);
  if (!material) return 'TBD';

  return `${material.productionDays} business days`;
}

// ============================================
// HELPER: Get edition by ID
// ============================================
export function getPhysicalEdition(editionId: SonicPhysicalEditionId) {
  return SONIC_PHYSICAL_EDITIONS.find(e => e.id === editionId);
}

// ============================================
// HELPER: Get recommended edition
// ============================================
export function getRecommendedEdition() {
  return SONIC_PHYSICAL_EDITIONS.find(e => e.isRecommended) || SONIC_PHYSICAL_EDITIONS[0];
}
